import React, {
  createContext,
  useContext,
  useRef,
  useEffect,
  useState,
  useMemo,
} from "react";

export type AnalyticsData = { [key: string]: any };

export type EventOptions = {
  delay?: number;
  overrides?: Partial<AnalyticsData>;
  onComplete?: (eventData: AnalyticsData) => void;
};

type FeesAnalyticsContextType = {
  analyticsData: AnalyticsData;
  setAnalyticsData: (updates: Partial<AnalyticsData>) => void;
  resetAnalyticsData: () => void;
  getAnalyticsData: () => AnalyticsData;
  mergeAnalyticsData: (options: EventOptions) => void;
};

const FeesAnalyticsContext = createContext<
  FeesAnalyticsContextType | undefined
>(undefined);

export const FeesAnalyticsDataProvider: React.FC<{
  children: React.ReactNode;
  initialData?: AnalyticsData;
}> = ({ children, initialData = {} }) => {
  const [analyticsData, setAnalyticsData] =
    useState<AnalyticsData>(initialData); // NOSONAR
  const analyticsRef = useRef(analyticsData);

  useEffect(() => {
    analyticsRef.current = analyticsData;
  }, [analyticsData]);

  const updateAnalyticsData = (updates: Partial<AnalyticsData>) => {
    setAnalyticsData((prev) => {
      const updated = { ...prev, ...updates };
      return updated;
    });
  };

  const resetAnalyticsData = () => setAnalyticsData({});

  const getAnalyticsData = () => analyticsRef.current;

  const mergeAnalyticsData = ({
    delay = 300,
    overrides = {},
    onComplete,
  }: EventOptions) => {
    setTimeout(() => {
      const finalData = { ...getAnalyticsData(), ...overrides };
      onComplete?.(finalData);
    }, delay);
  };

  const contextValue = useMemo(
    () => ({
      analyticsData,
      setAnalyticsData: updateAnalyticsData,
      resetAnalyticsData,
      getAnalyticsData,
      mergeAnalyticsData,
    }),
    [analyticsData],
  );

  return (
    <FeesAnalyticsContext.Provider value={contextValue}>
      {children}
    </FeesAnalyticsContext.Provider>
  );
};

export const useFeesAnalytics = (): FeesAnalyticsContextType => {
  const context = useContext(FeesAnalyticsContext);
  if (!context) {
    throw new Error(
      "useFeesAnalytics must be used within FeesAnalyticsDataProvider",
    );
  }
  return context;
};
